// The university lens (roadmap 11.P9, the v1's Egyetem tab): the Előrehaladás (what to do next), the semester's
// progress, the next classes and exams (ZH / exam added by hand too), the subjects with their editing form, and the
// two syncs: the Neptun calendar and the owner's study sheet on Drive (two-way).
import { useCallback, useEffect, useState } from 'react';
import { api, onChange } from '../api';
import { day, Label, Panel } from '../ui';

const STATE: Record<string, string> = { current: 'Ebben a félévben', behind: 'Pótolandó', planned: 'Későbbi félévre felvéve', later: 'Még hátravan', done: 'Kész', skipped: 'Nem kell (nem választott)' };
const LEVEL: Record<string, string> = { red: 'coral', yellow: 'amber', green: 'mint', info: 'cyan' };
const ICON: Record<string, string> = { exam: '🎓', zh: '📝', study: '📚', signature: '✍️', signup: '🗓️', behind: '⏪', task: '✅' };
const KIND: Array<[string, string]> = [['zh', 'ZH / dolgozat'], ['exam', 'Vizsga'], ['class', 'Óra / konzultáció']];

/** 'ma 10:00', 'nov. 6. 08:30' from 'YYYY-MM-DD HH:MM'. */
const when = (s: string | null | undefined) => {
  if (!s) return '';
  const t = s.slice(11, 16);
  return `${day(s.slice(0, 10))}${t && t !== '00:00' && t !== '23:59' ? ` ${t}` : ''}`;
};

function Item({ x, big, onOpen }: { x: any; big?: boolean; onOpen: (x: any) => void }) {
  const c = LEVEL[x.level] ?? 'cyan';
  return (
    <button type="button" className={`tx-row cfg-row uni-item ${big ? 'big' : ''}`} onClick={() => onOpen(x)}>
      <span className="row-split">
        <span>{ICON[x.kind] ?? '•'} {x.title}</span>
        {x.date ? <span className="j-chip" style={{ '--c': `var(--${c})` } as any}>{when(x.date)}</span>
          : x.level === 'red' ? <span className="j-chip" style={{ '--c': 'var(--coral)' } as any}>sürgős</span> : null}
      </span>
      {x.sub && <span className="small muted">{x.sub}</span>}
    </button>
  );
}

type Opt = readonly string[];
function SubjectForm({ s, options, onDone, onEvent }: { s: any; options: { signature: Opt; exam: Opt; status: Opt }; onDone: () => void; onEvent?: () => void }) {
  const isNew = s.id == null;
  const [f, setF] = useState<any>({ ...s, exam_date: s.exam_date ? String(s.exam_date).replace(' ', 'T') : '' });
  const [err, setErr] = useState('');
  const [sure, setSure] = useState(false);
  const set = (k: string, v: any) => setF((x: any) => ({ ...x, [k]: v }));
  const text = (k: string, label: string, hint?: string, type = 'text') => (
    <label key={k}>{label}<input className="j-input" type={type} value={f[k] ?? ''} placeholder={hint} onChange={(e) => set(k, e.target.value)} /></label>
  );
  const sel = (k: string, label: string, opts: Opt) => (
    <label key={k}>{label}<select className="j-input" value={f[k] == null ? '' : String(f[k])} onChange={(e) => set(k, e.target.value)}>{opts.map((o) => <option key={o} value={o}>{o || '–'}</option>)}</select></label>
  );
  const save = async () => {
    setErr('');
    const body = {
      name: String(f.name ?? '').trim(), rec_semester: f.rec_semester ?? '', semester: f.semester ?? '', credits: f.credits ?? '', signature: f.signature ?? '', exam: f.exam ?? '',
      grade: f.grade ?? '', zh: f.zh ?? '', exam_date: f.exam_date ? String(f.exam_date).replace('T', ' ') : '', signup_from: f.signup_from ?? '', signup_until: f.signup_until ?? '',
      aliases: f.aliases ?? '', notes: f.notes ?? '', status: f.status ?? '', code: f.code ?? '',
    };
    try {
      if (isNew) await api('/api/uni/subjects', body); else await api(`/api/uni/subjects/${s.id}`, body, { method: 'PATCH' });
      onDone();
    } catch (e: any) { setErr(e.message); }
  };
  const del = async () => { try { await api(`/api/uni/subjects/${s.id}`, undefined, { method: 'DELETE' }); onDone(); } catch (e: any) { setErr(e.message); } };
  return (
    <form className="money-form" onSubmit={(e) => { e.preventDefault(); save(); }}>
      {!isNew && <p className="small muted">{STATE[s.state] ?? ''}{s.source ? ` · forrás: ${s.source}` : ''}</p>}
      {text('name', 'Név', 'a tárgy neve')}
      <div className="j-row">{text('rec_semester', 'Ajánlott félév', '', 'number')}{text('semester', 'Felvéve (félév)', '', 'number')}{text('credits', 'Kredit', '', 'number')}</div>
      <div className="j-row">{sel('signature', 'Aláírás', options.signature)}{sel('exam', 'Vizsga', options.exam)}{sel('grade', 'Jegy', ['', '5', '4', '3', '2', '1'])}</div>
      {text('zh', 'ZH', 'pl. dec. 12. 12:30, vagy „nem lesz”')}
      {text('exam_date', 'Vizsga időpontja (ha jelentkeztél)', '', 'datetime-local')}
      <div className="j-row">{text('signup_from', 'Jelentkezés ettől', '', 'date')}{text('signup_until', 'eddig', '', 'date')}</div>
      {text('code', 'Neptun-kód', 'pl. GEIAL301-B')}
      {text('aliases', 'Más nevek', 'más írásmód a Neptunban / levelekben, vesszővel')}
      <label>Jegyzet<textarea className="j-input" rows={3} value={f.notes ?? ''} placeholder="Az aláírás feltétele, beadandó, a tanár elérhetősége…" onChange={(e) => set('notes', e.target.value)} /></label>
      {sel('status', 'Állapot (kész = teljesítve, nem kell = nem veszem fel)', options.status)}
      {err && <p className="err small">{err}</p>}
      <div className="j-row">
        <button className="j-btn j-btn--primary" type="submit">Mentés</button>
        {!isNew && onEvent && <button type="button" className="j-btn" onClick={onEvent}>+ ZH / vizsga</button>}
        {!isNew && <button type="button" className={`j-btn ${sure ? 'danger' : ''}`} onClick={() => (sure ? del() : setSure(true))}>{sure ? 'Biztosan törlöd?' : 'Törlés'}</button>}
        <button type="button" className="j-btn linkish" onClick={onDone}>Mégse</button>
      </div>
    </form>
  );
}

function EventForm({ subjects, subjectId, onDone }: { subjects: any[]; subjectId?: number | null; onDone: () => void }) {
  const today = new Date().toLocaleDateString('sv-SE');
  const [f, setF] = useState<any>({ subject_id: subjectId ?? '', kind: 'zh', date: today, time: '08:00', location: '' });
  const [err, setErr] = useState('');
  const set = (k: string, v: any) => setF((x: any) => ({ ...x, [k]: v }));
  const save = async () => { setErr(''); try { await api('/api/uni/events', f); onDone(); } catch (e: any) { setErr(e.message); } };
  return (
    <form className="money-form" onSubmit={(e) => { e.preventDefault(); save(); }}>
      <label>Tárgy<select className="j-input" value={f.subject_id} onChange={(e) => set('subject_id', e.target.value)}>
        <option value="">– válassz tárgyat –</option>{subjects.filter((s) => s.state !== 'done').map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select></label>
      <label>Mi?<select className="j-input" value={f.kind} onChange={(e) => set('kind', e.target.value)}>{KIND.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></label>
      <div className="j-row">
        <label>Nap<input className="j-input" type="date" value={f.date} onChange={(e) => set('date', e.target.value)} /></label>
        <label>Idő<input className="j-input" type="time" value={f.time} onChange={(e) => set('time', e.target.value)} /></label>
      </div>
      <label>Hol?<input className="j-input" value={f.location} placeholder="terem (nem kötelező)" onChange={(e) => set('location', e.target.value)} /></label>
      {err && <p className="err small">{err}</p>}
      <div className="j-row"><button className="j-btn j-btn--primary" type="submit">Mentés</button><button type="button" className="j-btn linkish" onClick={onDone}>Mégse</button></div>
    </form>
  );
}

function Sources({ d, reload }: { d: any; reload: () => void }) {
  const [msg, setMsg] = useState('');
  const run = async (path: string, done: (r: any) => string) => {
    setMsg('');
    try { const r = await api(path, {}); setMsg(r?.queued ? 'Elindítva, hamarosan kész.' : done(r)); reload(); } catch (e: any) { setMsg(e.message); }
  };
  const cal = d.calendar ?? {};
  const sh = d.sheet ?? {};
  const access = sh.canEdit == null ? 'még nem ellenőrzött' : sh.canEdit ? 'szerkesztheti' : 'csak olvashatja (a változásokat nem tudja beírni, adj Szerkesztő jogot)';
  return (
    <Panel c="violet"><Label c="violet">Források</Label>
      <p className="small">{!cal.configured ? 'A Neptun-naptár linkje nincs megadva (Beállítások → Egyetem).'
        : `Neptun-naptár: ${cal.at ? (cal.ok ? `frissítve ${cal.at}, ${cal.total} esemény` : `hiba (${cal.at}): ${cal.error}`) : 'még nem frissült'} · óránként magától.`}</p>
      {cal.configured && <button type="button" className="j-btn" onClick={() => run('/api/uni/sync', (r) => (r.ok ? `Kész: ${r.total} esemény, ${r.fresh ?? 0} új, ${r.gone ?? 0} elmarad.` : `Hiba: ${r.error}`))}>Frissítés most</button>}
      <p className="small" style={{ marginTop: 12 }}>{!sh.configured ? 'A tanulmányi Excel (Drive-link) nincs megadva (Beállítások → Egyetem).' : <>
        Tanulmányi Excel: {sh.name || 'megadva'}{sh.account ? <> · a Jarvis fiókja: <code>{sh.account}</code></> : null} · hozzáférés: {access}<br />
        {sh.at ? (sh.ok ? `Szinkron: ${sh.at}${sh.last ? ` (beírva ${sh.last.written}, onnan átvéve ${sh.last.fromSheet}${sh.last.waiting ? `, ${sh.last.waiting} vár írásra` : ''})` : ''}`
          : <span className="err">Hiba ({sh.at}): {sh.error}</span>) : 'Még nem volt szinkron.'}
        {sh.last?.unmatched?.length ? <><br /><span className="muted">Nem párosított sorok: {sh.last.unmatched.join(', ')}</span></> : null}</>}</p>
      {sh.configured && <button type="button" className="j-btn" onClick={() => run('/api/uni/sheet/sync', (r) => (r.ok === false ? `Hiba: ${r.error}` : r.last ? `Kész: ${r.last.written} cella beírva, ${r.last.fromSheet} változás átvéve.` : 'Kész.'))}>Szinkron most</button>}
      <p className="muted small">Oda-vissza, óránként és pár másodperccel a módosítás után: amit itt átírsz, bekerül az Excelbe; amit kézzel írsz az Excelbe, átveszem. Ha mindkét helyen változott, az itteni nyer.</p>
      {msg && <p className="small">{msg}</p>}
    </Panel>
  );
}

export function UniLens() {
  const [d, setD] = useState<any>(null);
  const [err, setErr] = useState('');
  const [open, setOpen] = useState<string | null>(null); // 's:<id>' in the list, 'n:<id>' from the Előrehaladás, 's:new', 'e:new', 'e:<subjectId>'
  const [all, setAll] = useState(false);
  const load = useCallback(() => api('/api/lens/uni').then(setD).catch((e) => setErr(e.message)), []);
  useEffect(() => { load(); return onChange(load); }, [load]);
  if (!d) return err ? <p className="err">{err}</p> : <p className="muted">Betöltés…</p>;
  const o = d.overview;
  const p = o.progress;
  const [first, ...rest] = o.next as any[];
  const shown = all ? rest : rest.slice(0, 6);
  const done = () => { setOpen(null); load(); };
  const subject = (id: number | null | undefined) => o.subjects.find((s: any) => s.id === id);
  const openItem = (x: any) => { if (x.subjectId != null && subject(x.subjectId)) setOpen(`n:${x.subjectId}`); else if (x.taskRef) location.hash = '#/most'; };
  const form = (s: any) => <SubjectForm s={s} options={d.options} onDone={done} onEvent={() => setOpen(`e:${s.id}`)} />;
  const pct = p.total ? Math.round((100 * p.done) / p.total) : 0;
  const openSubject = open?.startsWith('n:') ? subject(Number(open.slice(2))) : null;
  return (
    <div className="j-stack">
      <Panel c="cyan" className="j-next"><Label c="cyan">Előrehaladás</Label>
        {first ? <Item x={first} big onOpen={openItem} /> : <p className="muted">Most nincs teendő az egyetemmel.</p>}
        {rest.length > 0 && <p className="small muted" style={{ margin: '10px 0 0' }}>Utána</p>}
        {shown.map((x: any, i: number) => <Item key={i} x={x} onOpen={openItem} />)}
        {rest.length > 6 && <button type="button" className="j-btn more" onClick={() => setAll(!all)}>{all ? 'Kevesebb' : `Mind (${rest.length})`}</button>}
        {openSubject && <div><p className="small" style={{ margin: '10px 0 0' }}><b>{openSubject.name}</b></p>{form(openSubject)}</div>}
        {open?.startsWith('e:') ? <EventForm subjects={o.subjects} subjectId={open === 'e:new' ? null : Number(open.slice(2))} onDone={done} />
          : <div className="j-row" style={{ marginTop: 10 }}><button type="button" className="j-btn" onClick={() => setOpen('e:new')}>+ ZH / vizsga</button></div>}
      </Panel>
      <Panel c="mint"><Label c="mint">{o.semester}. félév · {p.done} / {p.total} tárgy kész</Label>
        <div className="bar"><i style={{ width: `${pct}%` }} /></div>
        <p className="small muted">{[`Ebben a félévben ${p.currentCount} tárgy`, p.behind ? `${p.behind} pótolandó` : null,
          p.credits.total ? `${p.credits.done} / ${p.credits.total} kredit` : 'a krediteket a tárgyaknál tudod megadni'].filter(Boolean).join(' · ')}</p>
      </Panel>
      {o.exams.length > 0 && (
        <Panel c="coral"><Label c="coral">Vizsgák, ZH-k · {o.exams.length}</Label>
          {o.exams.map((e: any) => (
            <div key={e.uid} className="j-slot"><div className="t">{when(e.start)}</div>
              <div><div>{e.kind === 'exam' ? 'Vizsga' : 'ZH'}: {e.subject || e.title}</div>
                <div className="w">{[e.detail, e.location, e.teacher, e.source === 'kézi' ? 'kézzel felvéve' : null].filter(Boolean).join(' · ')}
                  {e.source === 'kézi' && <> · <button type="button" className="j-btn linkish" style={{ padding: 0 }} onClick={async () => { await api(`/api/uni/events/${encodeURIComponent(e.uid)}`, undefined, { method: 'DELETE' }); load(); }}>törlés</button></>}</div></div></div>
          ))}
        </Panel>
      )}
      {o.classes.length > 0 && (
        <Panel c="cyan"><Label c="cyan">Következő órák</Label>
          {o.classes.map((e: any) => (
            <div key={e.uid} className="j-slot"><div className="t">{when(e.start)}</div>
              <div><div>{e.subject || e.title}</div><div className="w">{[e.location, e.teacher].filter(Boolean).join(' · ')}</div></div></div>
          ))}
        </Panel>
      )}
      <Panel c="amber"><Label c="amber">Tárgyak · {o.subjects.length}</Label>
        {['current', 'behind', 'planned', 'later', 'done', 'skipped'].map((g) => {
          const list = o.subjects.filter((s: any) => s.state === g);
          if (!list.length) return null;
          return (
            <details key={g} className="sources" open={g === 'current' || g === 'behind'}>
              <summary>{STATE[g]} ({list.length})</summary>
              {list.map((s: any) => (
                <div key={s.id} className="tx">
                  <button type="button" className="tx-row cfg-row" onClick={() => setOpen(open === `s:${s.id}` ? null : `s:${s.id}`)}>
                    <span className="row-split"><span>{s.name}</span><span className="muted small">{s.state === 'done' ? '✓' : s.state === 'behind' ? '!' : ''}</span></span>
                    <span className="small muted">{[s.rec_semester ? `${s.rec_semester}. félév` : null, s.credits ? `${s.credits} kr` : null, s.signature ? `aláírás: ${s.signature}` : null,
                      s.exam ? `vizsga: ${s.exam}` : null, s.grade ? `jegy: ${s.grade}` : null].filter(Boolean).join(' · ')}</span>
                  </button>
                  {open === `s:${s.id}` && form(s)}
                </div>
              ))}
            </details>
          );
        })}
        {!o.subjects.length && <p className="muted">Még nincs tárgy.</p>}
        {open === 's:new' ? <SubjectForm s={{ name: '', signature: '', exam: '', status: '' }} options={d.options} onDone={done} />
          : <button type="button" className="j-btn more" onClick={() => setOpen('s:new')}>+ Új tárgy</button>}
      </Panel>
      <Sources d={d} reload={load} />
    </div>
  );
}
